import { ApplicationConfig, provideBrowserGlobalErrorListeners, provideZoneChangeDetection, importProvidersFrom, APP_INITIALIZER, inject } from '@angular/core';
import { provideRouter, withInMemoryScrolling } from '@angular/router';
import { provideHttpClient, withFetch, withInterceptors, HttpClient, withXsrfConfiguration } from '@angular/common/http';
import { provideAnimations } from '@angular/platform-browser/animations';
import { TranslateModule, TranslateService, TranslateLoader } from '@ngx-translate/core';
import { Observable, firstValueFrom } from 'rxjs';

import { routes } from './app.routes';
import { provideClientHydration, withEventReplay } from '@angular/platform-browser';
import { authInterceptor } from './core/interceptors/auth.interceptor';
import { errorInterceptor } from './core/interceptors/error.interceptor';
import { loadingInterceptor } from './core/interceptors/loading.interceptor';
import { AuthService } from './core/services/auth.service';
import { SubUserService } from './core/services/sub-user.service';
import { EmergencyContactService } from './core/services/emergency-contact.service';
import { ProfileService } from './core/services/profile.service';
import { NotificationService } from './core/services/notification.service';
import { CookieService } from './core/services/cookie.service';
import { MultiTranslateHttpLoaderFactory } from './core/services/multi-translate-loader.service';

// Auth initialization factory
export function initializeAuth(authService: AuthService): () => Promise<void> {
  return () => authService.initializeAuth();
}

// Custom HttpLoader for translations with absolute path
export class CustomTranslateHttpLoader implements TranslateLoader {
  constructor(private http: HttpClient, private prefix: string = '/assets/i18n/', private suffix: string = '.json') {}

  getTranslation(lang: string): Observable<any> {
    return this.http.get(`${this.prefix}${lang}${this.suffix}`);
  }
}

// HttpLoaderFactory for translations
// Using MultiTranslateHttpLoader to load multiple translation files
export function HttpLoaderFactory(http: HttpClient): TranslateLoader {
  return MultiTranslateHttpLoaderFactory(http);
}

// Translation initialization factory
export function initializeTranslations(): () => Promise<void> {
  return () => {
    const translate = inject(TranslateService);
    translate.setDefaultLang('en');
    translate.setFallbackLang('en');
    return firstValueFrom(translate.use('en')).then(() => {
    }).catch((error) => {
      return Promise.resolve();
    });
  };
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideAnimations(),
    provideBrowserGlobalErrorListeners(),
    provideZoneChangeDetection({ eventCoalescing: true }),
    {
      provide: APP_INITIALIZER,
      useFactory: initializeAuth,
      deps: [AuthService],
      multi: true
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeTranslations,
      multi: true
    },
    AuthService,
    SubUserService,
    EmergencyContactService,
    ProfileService,
    NotificationService,
    CookieService,
    provideRouter(
      routes,
      withInMemoryScrolling({
        scrollPositionRestoration: 'enabled',
        anchorScrolling: 'enabled'
      })
    ),
    provideClientHydration(withEventReplay()),
    provideHttpClient(
      withFetch(),
      withXsrfConfiguration({
        cookieName: 'XSRF-TOKEN',
        headerName: 'X-CSRF-Token',
      }),
      withInterceptors([authInterceptor, errorInterceptor, loadingInterceptor])
    ),
    importProvidersFrom(
      TranslateModule.forRoot({
        defaultLanguage: 'en',
        fallbackLang: 'en',
        loader: {
          provide: TranslateLoader,
          useFactory: HttpLoaderFactory,
          deps: [HttpClient]
        }
      })
    )
  ]
};
